import { BusMessage, calcStringByteLength, MessageFactory, calcArrayBufferByteLength, INT32_BYTES_PER_ELEMENT } from "flavr-common";
import { calcFileIdByteLength, serializationFileId } from "../../modules/files/FileTypes";
/**
 * Transfers peer-to-peer file data or metadata between clients.
 */
export class PeerFileMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(clientId, fileId, data, creator, owner) {
        super();
        this.clientId = clientId;
        this.fileId = fileId;
        this.data = data;
        this.creator = creator;
        this.owner = owner;
    }
    serialization() { }
    static is(msg) {
        return msg.type === PeerFileMessage.typestatic;
    }
}
PeerFileMessage.typestatic = "PeerFileMessage";
PeerFileMessage.subjectstatic = "local";
MessageFactory.register(PeerFileMessage);
/**
 * Announces that a peer file transfer was closed.
 */
export class PeerFileClosedMessage extends BusMessage {
    get byteLength() {
        return calcFileIdByteLength(this.fileId);
    }
    constructor(fileId) {
        super();
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = serializationFileId(stream, this.fileId);
    }
    static is(msg) {
        return msg.type === PeerFileClosedMessage.typestatic;
    }
}
PeerFileClosedMessage.typestatic = "PeerFileClosedMessage";
PeerFileClosedMessage.subjectstatic = "local";
MessageFactory.register(PeerFileClosedMessage);
/**
 * Announces that a peer file data channel was opened.
 */
export class PeerFileChannelOpenedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(clientId, fileId, filename) {
        super();
        this.clientId = clientId;
        this.fileId = fileId;
        this.filename = filename;
    }
    serialization() { }
    static is(msg) {
        return msg.type === PeerFileChannelOpenedMessage.typestatic;
    }
}
PeerFileChannelOpenedMessage.typestatic = "PeerFileChannelOpenedMessage";
PeerFileChannelOpenedMessage.subjectstatic = "local";
MessageFactory.register(PeerFileChannelOpenedMessage);
/**
 * Announces that a peer file data channel was closed.
 */
export class PeerFileChannelClosedMessage extends BusMessage {
    get byteLength() {
        return 0;
    }
    constructor(clientId, fileId) {
        super();
        this.clientId = clientId;
        this.fileId = fileId;
    }
    serialization() { }
    static is(msg) {
        return msg.type === PeerFileChannelClosedMessage.typestatic;
    }
}
PeerFileChannelClosedMessage.typestatic = "PeerFileChannelClosedMessage";
PeerFileChannelClosedMessage.subjectstatic = "local";
MessageFactory.register(PeerFileChannelClosedMessage);
/**
 * Reports an error that occurred during peer file transfer.
 */
export class PeerFileErrorMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.clientId) + calcFileIdByteLength(this.fileId) + calcStringByteLength(this.error);
    }
    constructor(clientId, fileId, error) {
        super();
        this.clientId = clientId;
        this.fileId = fileId;
        this.error = error;
    }
    serialization(stream) {
        this.clientId = stream.utf16String(this.clientId);
        this.fileId = serializationFileId(stream, this.fileId);
        this.error = stream.utf16String(this.error);
    }
    static is(msg) {
        return msg.type === PeerFileErrorMessage.typestatic;
    }
}
PeerFileErrorMessage.typestatic = "PeerFileErrorMessage";
PeerFileErrorMessage.subjectstatic = "local";
MessageFactory.register(PeerFileErrorMessage);
/**
 * Requests metadata for a peer-shared file.
 */
export class RequestPeerFileInfoMessage extends BusMessage {
    get byteLength() {
        return calcFileIdByteLength(this.fileId);
    }
    constructor(fileId) {
        super();
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = serializationFileId(stream, this.fileId);
    }
    static is(msg) {
        return msg.type === RequestPeerFileInfoMessage.typestatic;
    }
}
RequestPeerFileInfoMessage.typestatic = "RequestPeerFileInfoMessage";
RequestPeerFileInfoMessage.subjectstatic = "peer";
MessageFactory.register(RequestPeerFileInfoMessage);
/**
 * Returns metadata for a peer-shared file.
 */
export class ResponsePeerFileInfoMessage extends BusMessage {
    get byteLength() {
        return calcFileIdByteLength(this.fileId) + calcStringByteLength(this.ownerClientId);
    }
    constructor(fileId, ownerClientId) {
        super();
        this.fileId = fileId;
        this.ownerClientId = ownerClientId;
    }
    serialization(stream) {
        this.fileId = serializationFileId(stream, this.fileId);
        this.ownerClientId = stream.utf16StringSafe(this.ownerClientId);
    }
    static is(msg) {
        return msg.type === ResponsePeerFileInfoMessage.typestatic;
    }
}
ResponsePeerFileInfoMessage.typestatic = "ResponsePeerFileInfoMessage";
ResponsePeerFileInfoMessage.subjectstatic = "peer";
MessageFactory.register(ResponsePeerFileInfoMessage);
/**
 * Requests a chunk of peer-shared file data.
 */
export class RequestPeerFileDataMessage extends BusMessage {
    get byteLength() {
        return calcFileIdByteLength(this.fileId);
    }
    constructor(fileId) {
        super();
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = serializationFileId(stream, this.fileId);
    }
    static is(msg) {
        return msg.type === RequestPeerFileDataMessage.typestatic;
    }
}
RequestPeerFileDataMessage.typestatic = "RequestPeerFileDataMessage";
RequestPeerFileDataMessage.subjectstatic = "peer";
MessageFactory.register(RequestPeerFileDataMessage);
/**
 * Announces that a file was dropped into the client.
 */
export class DropFileMessage extends BusMessage {
    get byteLength() {
        return (calcStringByteLength(this.url) +
            calcStringByteLength(this.name) +
            calcStringByteLength(this.type) +
            Uint32Array.BYTES_PER_ELEMENT + // size
            INT32_BYTES_PER_ELEMENT // lastModified
        );
    }
    constructor(url, name, type, size, lastModified) {
        super();
        this.url = url;
        this.name = name;
        this.type = type;
        this.size = size;
        this.lastModified = lastModified;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
        this.name = stream.utf16String(this.name);
        this.type = stream.utf16String(this.type);
        this.size = stream.uint32(this.size);
        this.lastModified = stream.int32(this.lastModified);
    }
    static is(msg) {
        return msg.type === DropFileMessage.typestatic;
    }
}
DropFileMessage.typestatic = "FileDropMessage";
DropFileMessage.subjectstatic = "general";
MessageFactory.register(DropFileMessage);
/**
 * Revokes an object URL that was created for a dropped file.
 */
export class RevokeDropFileUrlMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.url);
    }
    constructor(url) {
        super();
        this.url = url;
    }
    serialization(stream) {
        this.url = stream.utf16String(this.url);
    }
    static is(msg) {
        return msg.type === RevokeDropFileUrlMessage.typestatic;
    }
}
RevokeDropFileUrlMessage.typestatic = "RevokeDropFileUrlMessage";
RevokeDropFileUrlMessage.subjectstatic = "general";
MessageFactory.register(RevokeDropFileUrlMessage);
/**
 * Adds file data to the local file handling pipeline.
 */
export class AddFileDataMessage extends BusMessage {
    get byteLength() {
        return calcArrayBufferByteLength(this.data) + calcStringByteLength(this.fileName) + calcStringByteLength(this.fileTypeExtension);
    }
    constructor(fileName, data, fileTypeExtension) {
        super();
        this.fileName = fileName;
        this.data = data;
        this.fileTypeExtension = fileTypeExtension;
    }
    serialization(stream) {
        this.fileName = stream.utf16String(this.fileName);
        this.data = stream.arrayBuffer(this.data);
        this.fileTypeExtension = stream.utf16StringSafe(this.fileTypeExtension);
    }
    static is(msg) {
        return msg.type === AddFileDataMessage.typestatic;
    }
}
AddFileDataMessage.typestatic = "AddFileDataMessage";
AddFileDataMessage.subjectstatic = "general";
MessageFactory.register(AddFileDataMessage);
/**
 * Returns the result of adding file data to the local file handling pipeline.
 */
export class AddFileDataResponseMessage extends BusMessage {
    get byteLength() {
        return calcFileIdByteLength(this.fileId);
    }
    constructor(fileId) {
        super();
        this.fileId = fileId;
    }
    serialization(stream) {
        this.fileId = serializationFileId(stream, this.fileId);
    }
    static is(msg) {
        return msg.type === AddFileDataResponseMessage.typestatic;
    }
}
AddFileDataResponseMessage.typestatic = "AddFileDataResponseMessage";
AddFileDataResponseMessage.subjectstatic = "general";
MessageFactory.register(AddFileDataResponseMessage);
